import React from 'react';
import { CreditCard, Lock, LogOut } from 'lucide-react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BranchProvider } from './contexts/BranchContext';

import Layout from './components/Layout/Layout';
import Login from './pages/Auth/Login';
import EnhancedDashboard from './pages/Dashboard/EnhancedDashboard';
import Jobs from './pages/Jobs/Jobs';
import JobDetails from './pages/Jobs/JobDetails';
import CreateJob from './pages/Jobs/CreateJob';
import Customers from './pages/Customers/Customers';
import Products from './pages/Products/Products';
import Inventory from './pages/Inventory/Inventory';
import Expenses from './pages/Expenses/Expenses';
import Reports from './pages/Reports/Reports';
import Settings from './pages/Settings/Settings';
import Statement from './pages/Ledger/Statement';
import OAuth2Callback from './pages/Settings/OAuth2Callback';
import SaasPortal from './pages/SaaS/SaaSPortal';


// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route Component (redirect if authenticated to role-specific dashboard)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="spinner w-8 h-8 text-cyan-500"></div>
      </div>
    );
  }

  if (user) {
    if (user.role === 'SUPER_ADMIN') {
      return <Navigate to="/saas/superadmin" replace />;
    }
    if (user.role === 'DISTRIBUTOR') {
      return <Navigate to="/saas/distributor" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Premium subscription lockout screen for expired client stores
const ExpiredLockoutScreen = () => {
  const { user, logout, mockPaymentUnlock, currencySymbol, currencyCode } = useAuth();
  const [isPaying, setIsPaying] = React.useState(false);

  const handleMockPay = () => {
    setIsPaying(true);
    setTimeout(() => {
      mockPaymentUnlock();
      setIsPaying(false);
    }, 2000);
  };

  const getPlanPrice = () => {
    if (user?.planData?.price !== undefined) return Number(user.planData.price);
    if (user?.plan === 'Enterprise') return 249;
    if (user?.plan === 'Growth') return 99;
    return 49;
  };

  const getPlanCycleLabel = () => {
    const cycle = user?.planData?.cycle;
    return cycle === 'Yearly' ? 'yearly' : 'monthly';
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 relative overflow-hidden text-slate-100 font-sans">
      {/* Background neon glows */}
      <div className="absolute top-[-10%] left-[-10%] w-[50vw] h-[50vw] rounded-full bg-red-600/10 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[50vw] h-[50vw] rounded-full bg-orange-600/10 blur-[120px] pointer-events-none" />

      <div className="max-w-md w-full bg-slate-900/80 border border-red-500/20 rounded-3xl p-8 shadow-2xl backdrop-blur-xl relative z-10 text-center space-y-6">
        
        {/* Animated Radar Radar Lock Shield */}
        <div className="relative mx-auto w-20 h-20 flex items-center justify-center">
          <div className="absolute inset-0 rounded-full bg-red-500/10 animate-ping border border-red-500/20" />
          <div className="w-16 h-16 rounded-full bg-red-950/40 border border-red-500/40 flex items-center justify-center text-red-500 shadow-lg shadow-red-950/30">
            <Lock className="w-8 h-8 animate-pulse" />
          </div>
        </div>

        <div>
          <span className="bg-red-500/20 text-red-400 text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-widest border border-red-500/30">
            Subscription Lockout
          </span>
          <h2 className="text-2xl font-black text-white mt-3 tracking-tight">
            Store Access Expired
          </h2>
          <p className="text-slate-400 text-xs mt-2 leading-relaxed">
            The subscription plan for <strong className="text-slate-200">{user.shopName || 'your store'}</strong> has expired. 
            Access to your digital repair shop software, jobs logging, and customer ledgers is currently restricted.
          </p>
        </div>

        {/* Invoice pricing breakdown details */}
        <div className="bg-slate-950/80 border border-slate-800 rounded-2xl p-5 space-y-3 font-mono text-xs text-left">
          <div className="flex justify-between border-b border-slate-800 pb-2">
            <span className="text-slate-500">STORE ADMIN:</span>
            <span className="text-slate-300 font-bold">{user.firstName} {user.lastName}</span>
          </div>
          <div className="flex justify-between border-b border-slate-800 pb-2">
            <span className="text-slate-500">BILLING TIER:</span>
            <span className="text-red-400 font-bold">{user.plan || 'Starter'} Plan</span>
          </div>
          <div className="flex justify-between text-xs pt-1">
            <span className="text-emerald-400 font-bold">RENEWAL CHARGE:</span>
            <span className="text-emerald-400 font-black text-sm">{currencySymbol}{getPlanPrice()} {currencyCode} / {getPlanCycleLabel()}</span>
          </div>
        </div>

        {/* Payment and Logout Buttons */}
        <div className="space-y-3">
          <button
            onClick={handleMockPay}
            disabled={isPaying}
            className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 active:from-red-700 active:to-orange-700 text-white font-extrabold py-3.5 rounded-xl text-sm transition-all duration-200 flex items-center justify-center gap-2 border border-red-400/20 shadow-lg shadow-red-950/40"
          >
            {isPaying ? (
              <>
                <div className="spinner w-4 h-4 border-white" /> Authorizing Payment...
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4" /> Secure Checkout & Auto-Unlock
              </>
            )}
          </button>

          <button
            onClick={logout}
            className="w-full bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold py-3 rounded-xl text-xs transition-colors flex items-center justify-center gap-1.5"
          >
            <LogOut className="w-4 h-4" /> Sign In as Different User
          </button>
        </div>

        <p className="text-[10px] text-slate-500 leading-normal">
          *Billing calculations and renewal locks are administered dynamically. Unlocking processes payment instantly via mock gateway protocol.
        </p>

      </div>
    </div>
  );
};

// Route Switcher that guards views based on user type and handles expired states
const ProtectedHandler = () => {
  const { user } = useAuth();

  // 1. Super Admin View (fully isolated dashboard layout)
  if (user.role === 'SUPER_ADMIN') {
    return (
      <Routes>
        <Route path="/saas/superadmin" element={<SaasPortal />} />
        <Route path="*" element={<Navigate to="/saas/superadmin" replace />} />
      </Routes>
    );
  }

  // 2. Distributor View (fully isolated portal layout)
  if (user.role === 'DISTRIBUTOR') {
    return (
      <Routes>
        <Route path="/saas/distributor" element={<SaasPortal />} />
        <Route path="*" element={<Navigate to="/saas/distributor" replace />} />
      </Routes>
    );
  }

  // 3. Client Admin / Shop Staff
  // Incase the account plan is completely expired or unassigned, lock the page completely!
  if (user.role === 'CLIENT' && user.isExpired) {
    return <ExpiredLockoutScreen />;
  }

  // Active Client portal - render within normal repair shop sidebar structure
  return (
    <Layout>
      {/* Expiring Soon Banner Notification */}
      {user.role === 'CLIENT' && user.isExpiringSoon && (
        <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white py-2.5 px-4 text-center font-bold text-xs animate-pulse sticky top-0 z-[40] flex items-center justify-center gap-2 shadow-md">
          <span>⚠️ Your subscription plan will expire soon. Please contact your distributor to renew your account!</span>
        </div>
      )}
      
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<EnhancedDashboard />} />
        
        {/* Jobs Routes */}
        <Route path="/jobs" element={<Jobs />} />
        <Route path="/jobs/create" element={<CreateJob />} />
        <Route path="/jobs/:id" element={<JobDetails />} />
        
        {/* Other Routes */}
        <Route path="/customers" element={<Customers />} />
        <Route path="/products" element={<Products />} />
        <Route path="/inventory" element={<Inventory />} />
        <Route path="/expenses" element={<Expenses />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/statement" element={<Statement />} />
        <Route path="/settings" element={<Settings />} />
        
        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  );
};

// Subrouting layer
const AppRoutes = () => {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="spinner w-8 h-8 text-cyan-500"></div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public Login Route */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />

      {/* OAuth2 Callback Route */}
      <Route 
        path="/oauth2callback" 
        element={
          <ProtectedRoute>
            <OAuth2Callback />
          </ProtectedRoute>
        } 
      />

      {/* Protected Routes */}
      <Route 
        path="/*" 
        element={
          <ProtectedRoute>
            <ProtectedHandler />
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BranchProvider>
          <Router>
            <div className="App">
              <AppRoutes />
              
              {/* Toast notifications */}
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: '#1e293b',
                    color: '#fff',
                    border: '1px border border-slate-700'
                  },
                  success: {
                    duration: 3000,
                    theme: {
                      primary: '#06b6d4',
                      secondary: '#0f172a',
                    },
                  },
                }}
              />
            </div>
          </Router>
        </BranchProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;